'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { VStack, HStack } from '@terra/ui-styled-system';
import {
  Text,
  Field,
  Textarea,
  Select,
  createSelectListCollection,
  RichMarkdownEditor,
  formatCategoryForDisplay,
} from '@terra/ui-design-system';
import { z } from 'zod';
import {
  getSignalCategoriesForTargetType,
  SignalCategorySchema,
  SignalStatusSchema,
  defaultStageForSignalStatus,
  type SignalStatusType,
} from '@terra/shared-schemas-orchestrator';
import { useTargetType } from '../../store/projectStore';
import type {
  SignalEditableFieldKey,
  SignalEditableFields,
} from './signalEditFields';

type SignalEditFormProps = {
  editableFields: SignalEditableFields;
  onFieldChange: <T extends SignalEditableFieldKey>(
    field: T,
    value: SignalEditableFields[T],
  ) => void;
  saveError: string | null;
};

const statusOptions = createSelectListCollection({
  items: [
    { label: '—', value: '' },
    ...SignalStatusSchema.options.map((s) => ({ label: s, value: s })),
  ],
});

const confidenceOptions = createSelectListCollection({
  items: [1, 2, 3, 4, 5].map((value) => ({
    label: String(value),
    value: String(value),
  })),
});

const confidenceFieldSchema = z.int().min(1).max(5);

export function SignalEditForm({
  editableFields,
  onFieldChange,
  saveError,
}: SignalEditFormProps) {
  const t = useTranslations('designSystem.signals.editDrawer');
  const targetType = useTargetType();
  const categoryOptions = useMemo(() => {
    const categories = [
      ...getSignalCategoriesForTargetType(targetType ?? 'WEB_APPLICATION'),
    ];
    if (
      editableFields.category &&
      !categories.includes(editableFields.category)
    ) {
      categories.push(editableFields.category);
    }
    return createSelectListCollection({
      items: categories.map((category: string) => ({
        label: formatCategoryForDisplay(category),
        value: category,
      })),
    });
  }, [editableFields.category, targetType]);

  const confidenceValue = useMemo(
    () =>
      editableFields.confidence !== null &&
      editableFields.confidence !== undefined
        ? [String(editableFields.confidence)]
        : [],
    [editableFields.confidence],
  );

  return (
    <VStack gap={4} w="full" alignItems="stretch">
      {saveError && (
        <Text
          p={2}
          borderRadius="md"
          fontSize="xs"
          fontWeight="medium"
          borderWidth="1px"
          borderColor="border.error"
          color="fg.error"
          bg="bg.canvas"
        >
          {saveError}
        </Text>
      )}

      <VStack
        gap={3}
        p={4}
        bg="bg.default"
        borderRadius="md"
        borderWidth="1px"
        borderColor="border.default"
        w="full"
        alignItems="stretch"
      >
        <HStack w="full" justifyContent="space-between" alignItems="center">
          <Text fontSize="sm" fontWeight="semibold">
            {t('sections.signalInformation.title')}
          </Text>
          <Text fontSize="2xs" color="fg.muted">
            {t('sections.signalInformation.description')}
          </Text>
        </HStack>

        <Field.Root>
          <Field.Label fontSize="xs" fontWeight="medium">
            {t('sections.signalInformation.fields.description.label')}
          </Field.Label>
          <Field.HelperText fontSize="2xs" color="fg.muted">
            {t('sections.signalInformation.fields.description.helperText')}
          </Field.HelperText>
          <RichMarkdownEditor
            value={editableFields.description ?? ''}
            onChange={(value) => onFieldChange('description', value || null)}
            placeholder={t(
              'sections.signalInformation.fields.description.placeholder',
            )}
            height={200}
            minHeight={150}
            maxHeight={600}
            resizable
            preview="live"
          />
        </Field.Root>

        <HStack w="full" gap={3} alignItems="flex-start">
          <Field.Root flex={1}>
            <Field.Label fontSize="xs" fontWeight="medium">
              {t('sections.signalInformation.fields.category.label')}
            </Field.Label>
            <Field.HelperText fontSize="2xs" color="fg.muted">
              {t('sections.signalInformation.fields.category.helperText')}
            </Field.HelperText>
            <Select.Root
              positioning={{
                sameWidth: true,
                strategy: 'fixed',
              }}
              size="sm"
              collection={categoryOptions}
              value={[editableFields.category]}
              onValueChange={(details: { value: string[] }) => {
                const nextCategory = details.value[0];
                if (!nextCategory) {
                  return;
                }

                const parsed = SignalCategorySchema.safeParse(nextCategory);
                if (parsed.success) {
                  onFieldChange('category', parsed.data);
                }
              }}
            >
              <Select.Control>
                <Select.Trigger>
                  <Select.ValueText
                    placeholder={t(
                      'sections.signalInformation.fields.category.placeholder',
                    )}
                  />
                </Select.Trigger>
              </Select.Control>
              <Select.Positioner>
                <Select.Content>
                  <Select.ItemGroup>
                    {categoryOptions.items.map((item) => (
                      <Select.Item key={item.value} item={item}>
                        <Select.ItemText>{item.label}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </Select.ItemGroup>
                </Select.Content>
              </Select.Positioner>
            </Select.Root>
          </Field.Root>

          <Field.Root flex={1}>
            <Field.Label fontSize="xs" fontWeight="medium">
              {t('sections.signalInformation.fields.label.label')}
            </Field.Label>
            <Field.HelperText fontSize="2xs" color="fg.muted">
              {t('sections.signalInformation.fields.label.helperText')}
            </Field.HelperText>
            <Select.Root
              positioning={{
                sameWidth: true,
                strategy: 'fixed',
              }}
              size="sm"
              collection={statusOptions}
              value={editableFields.status ? [editableFields.status] : ['']}
              onValueChange={(details: { value: string[] }) => {
                const nextStatus = details.value[0];
                if (nextStatus === '' || nextStatus === undefined) {
                  onFieldChange('status', null);
                  onFieldChange(
                    'stage',
                    defaultStageForSignalStatus({ status: null }),
                  );
                  return;
                }
                const parsed = SignalStatusSchema.safeParse(nextStatus);
                if (parsed.success) {
                  const newStatus = parsed.data as SignalStatusType;
                  onFieldChange('status', newStatus);
                  onFieldChange(
                    'stage',
                    defaultStageForSignalStatus({ status: newStatus }),
                  );
                }
              }}
            >
              <Select.Control>
                <Select.Trigger>
                  <Select.ValueText
                    placeholder={t(
                      'sections.signalInformation.fields.label.placeholder',
                    )}
                  />
                </Select.Trigger>
              </Select.Control>
              <Select.Positioner>
                <Select.Content>
                  <Select.ItemGroup>
                    {statusOptions.items.map((item) => (
                      <Select.Item key={item.value} item={item}>
                        <Select.ItemText>{item.label}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </Select.ItemGroup>
                </Select.Content>
              </Select.Positioner>
            </Select.Root>
          </Field.Root>
        </HStack>

        <Field.Root>
          <Field.Label fontSize="xs" fontWeight="medium">
            {t('sections.signalInformation.fields.confidence.label')}
          </Field.Label>
          <Field.HelperText fontSize="2xs" color="fg.muted">
            {t('sections.signalInformation.fields.confidence.helperText')}
          </Field.HelperText>
          <Select.Root
            positioning={{
              sameWidth: true,
              strategy: 'fixed',
            }}
            size="sm"
            collection={confidenceOptions}
            value={confidenceValue}
            onValueChange={(details: { value: string[] }) => {
              const nextValue = details.value[0];
              if (!nextValue) {
                onFieldChange('confidence', null);
                return;
              }

              const parsed = confidenceFieldSchema.safeParse(Number(nextValue));
              if (parsed.success) {
                onFieldChange('confidence', parsed.data);
              }
            }}
          >
            <Select.Control>
              <Select.Trigger>
                <Select.ValueText
                  placeholder={t(
                    'sections.signalInformation.fields.confidence.placeholder',
                  )}
                />
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                <Select.ItemGroup>
                  {confidenceOptions.items.map((item) => (
                    <Select.Item key={item.value} item={item}>
                      <Select.ItemText>{item.label}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.ItemGroup>
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </Field.Root>
      </VStack>

      <VStack
        gap={3}
        p={4}
        bg="bg.default"
        borderRadius="md"
        borderWidth="1px"
        borderColor="border.default"
        w="full"
        alignItems="stretch"
      >
        <HStack w="full" justifyContent="space-between" alignItems="center">
          <Text fontSize="sm" fontWeight="semibold">
            {t('sections.analysisDetails.title')}
          </Text>
          <Text fontSize="2xs" color="fg.muted">
            {t('sections.analysisDetails.description')}
          </Text>
        </HStack>

        <Field.Root>
          <Field.Label fontSize="xs" fontWeight="medium">
            {t('sections.analysisDetails.fields.analysis.label')}
          </Field.Label>
          <Field.HelperText fontSize="2xs" color="fg.muted">
            {t('sections.analysisDetails.fields.analysis.helperText')}
          </Field.HelperText>
          <RichMarkdownEditor
            value={editableFields.analysis ?? ''}
            onChange={(value) => onFieldChange('analysis', value || null)}
            placeholder={t(
              'sections.analysisDetails.fields.analysis.placeholder',
            )}
            height={200}
            minHeight={150}
            maxHeight={600}
            resizable
            preview="live"
          />
        </Field.Root>

        <Field.Root>
          <Field.Label fontSize="xs" fontWeight="medium">
            {t('sections.analysisDetails.fields.reason.label')}
          </Field.Label>
          <Field.HelperText fontSize="2xs" color="fg.muted">
            {t('sections.analysisDetails.fields.reason.helperText')}
          </Field.HelperText>
          <Textarea
            size="sm"
            value={editableFields.reason ?? ''}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              onFieldChange('reason', event.target.value || null)
            }
            placeholder={t(
              'sections.analysisDetails.fields.reason.placeholder',
            )}
            rows={3}
          />
        </Field.Root>
      </VStack>
    </VStack>
  );
}
